import { useCallback, useEffect, useMemo, useState } from 'react';
import { Trash2, Plus, X } from 'lucide-react';
import { Alert, Button, Card, Input, Select, Switch } from '@dreamtree-org/twreact-ui';
import { useAppApi } from '@/lib/api';
import Can from '@rbac/Can';
import { writeActionFor } from '@rbac/writeAction';
import DataTable from '@/components/DataTable';
import PageShell from '@/components/layout/PageShell';
import PageHeader from '@/components/layout/PageHeader';

/**
 * "Embed Keys" (PRD §3.9) — its own nav item rather than a section of the AI Agents
 * page. Publishable keys are configured far more than they are read alongside an agent
 * list, and the per-key branding editor needs the whole page width.
 *
 * The route is generated from the `embed` module in modules.config.js (already declared:
 * models ['AppEmbedKey'], route /app-embed-key) and gated embed.view.org; the write
 * controls below are separately gated embed.rotate.org.
 */
function parseJsonArray(text) {
  try {
    const v = JSON.parse(text || '[]');
    return Array.isArray(v) ? v : [];
  } catch {
    return [];
  }
}

const STATUS_OPTIONS = [
  { label: 'All statuses', value: '' },
  { label: 'Active', value: 'active' },
  { label: 'Disabled', value: 'disabled' },
];

/** Publishable, so it goes in a customer's page source — random but not a secret.
 *  The identity secret (generated separately, shown once) is the real credential. */
function generatePublishableKey() {
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  return `pk_live_${Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')}`;
}

/** Comma/newline separated free text -> a clean JSON array string, or null when empty
 *  (empty allowed_origins deliberately means "any origin" — see the widget docs). */
function toJsonList(text) {
  const items = String(text || '').split(/[\n,]/).map((s) => s.trim()).filter(Boolean);
  return items.length ? JSON.stringify(items) : null;
}

function NewEmbedKeyForm({ onCreated, onCancel }) {
  const { fetchApi } = useAppApi();
  const [label, setLabel] = useState('');
  const [agentKeys, setAgentKeys] = useState('');
  const [origins, setOrigins] = useState('');
  const [applicationId, setApplicationId] = useState(null);
  const [apps, setApps] = useState([]);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    fetchApi('applicationsList').then((res) => setApps((Array.isArray(res) ? res : res?.data) || [])).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    setErr('');
    try {
      await fetchApi('crud', {
        model: 'AppEmbedKey', action: 'create',
        data: {
          key: generatePublishableKey(),
          label: label || null,
          agent_keys: toJsonList(agentKeys),
          allowed_origins: toJsonList(origins),
          application_id: applicationId || null,
          is_active: true,
        },
      });
      onCreated();
    } catch (ex) {
      setErr((ex.response && ex.response.data && ex.response.data.error) || ex.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="p-3 space-y-3 border-primary-300">
      <div className="font-semibold">New embed key</div>
      {err && <Alert variant="error" title="Could not create">{err}</Alert>}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Input label="Label" value={label} placeholder="e.g. Marketing site" onChange={(e) => setLabel(e.target.value)} />
        <Input label="Agent keys (comma separated)" value={agentKeys} placeholder="assistant, faq_bot" onChange={(e) => setAgentKeys(e.target.value)} />
        <Select
          label="Application"
          options={[{ label: 'Company-wide (all apps)', value: '' }, ...apps.map((a) => ({ label: a.name, value: a.id }))]}
          value={applicationId || ''}
          onChange={(v) => setApplicationId(v || null)}
          allowClear
          searchable
        />
      </div>
      <div className="grid grid-cols-1 gap-3">
        <Input label="Allowed origins (comma separated)" value={origins} placeholder="https://example.com" onChange={(e) => setOrigins(e.target.value)} />
      </div>
      <p className="text-xs text-secondary-500">
        Leave origins empty to allow any site — testing only. The key itself is generated for you.
      </p>
      <div className="flex items-center gap-2">
        <div className="flex-1" />
        <Button size="sm" variant="secondary" onClick={onCancel}>Cancel</Button>
        <Button size="sm" onClick={save} disabled={saving || !agentKeys.trim()}>{saving ? 'Creating…' : 'Create key'}</Button>
      </div>
    </Card>
  );
}

const MAX_PROMPTS = 6;
// Defaults live on the SERVER (services/embed/branding.js). These two default to ON, so an
// explicit `false` is meaningful and must survive the save — dropping every falsy value, as
// the original cleanup did, would silently re-enable whatever the admin just turned off.
const DEFAULT_ON_FLAGS = ['allow_expand', 'feedback', 'show_traces', 'voice_input'];

/** Strips the fields the admin left blank so an untouched key stores no branding at all,
 *  while preserving a deliberate `false` on the default-on flags. */
function cleanBranding(branding) {
  return Object.fromEntries(Object.entries(branding).filter(([field, value]) => {
    if (DEFAULT_ON_FLAGS.includes(field)) return value === false;
    if (Array.isArray(value)) return value.some((v) => String(v).trim());
    return value !== '' && value !== false && value != null;
  }));
}

/** Conversation starters. A row-per-prompt list rather than one comma-separated field:
 *  prompts routinely contain commas ("Compare A, B and C"), which a split would shred. */
function PromptListEditor({ prompts, onChange }) {
  const rows = prompts.length ? prompts : [''];
  const setAt = (index, value) => onChange(rows.map((p, i) => (i === index ? value : p)));
  return (
    <div className="space-y-2">
      <span className="text-sm font-medium">Suggested prompts</span>
      {rows.map((prompt, index) => (
        // Index-keyed on purpose: the value IS the text being edited, so it cannot key its own row.
        <div key={index} className="flex items-center gap-2">
          <Input className="flex-1" value={prompt} placeholder="Which denials are worth appealing?" onChange={(e) => setAt(index, e.target.value)} />
          <button
            type="button"
            aria-label="Remove prompt"
            className="p-1.5 rounded-lg text-secondary-500 hover:bg-[var(--app-border)]/40"
            onClick={() => onChange(rows.filter((_, i) => i !== index))}
          >
            <X size={14} />
          </button>
        </div>
      ))}
      <Button size="sm" variant="secondary" disabled={rows.length >= MAX_PROMPTS} onClick={() => onChange([...rows, ''])}>
        <Plus size={14} className="mr-1 inline" />Add prompt
      </Button>
    </div>
  );
}

const GRID = 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 items-end';

/** Plain labelled groups rather than twreact's Accordion: that component hardcodes
 *  `bg-white` on every panel, which would leave this page unreadable in dark mode. */
function FieldGroup({ title, children }) {
  return (
    <section className="rounded-lg border border-[var(--app-border)] p-3 space-y-3">
      <h4 className="text-xs font-semibold uppercase tracking-wide text-secondary-500">{title}</h4>
      {children}
    </section>
  );
}

function AppearanceFields({ branding, set }) {
  return (
    <div className={GRID}>
      <Input label="Widget title" value={branding.title || ''} onChange={(e) => set('title', e.target.value)} />
      <Input label="Subtitle" placeholder="Answers grounded in your docs" value={branding.subtitle || ''} onChange={(e) => set('subtitle', e.target.value)} />
      {/* Hex, not twreact's ColorPicker: that one emits `rgb(r, g, b)`, and the server's
          branding contract takes a bare hex only. The swatch is the feedback instead.
          A very pale brand reads badly behind the white text on user bubbles. */}
      <div className="flex items-end gap-2">
        <Input className="flex-1" label="Brand color (#hex)" placeholder="#4f46e5" value={branding.color || ''} onChange={(e) => set('color', e.target.value)} />
        <span
          aria-hidden="true"
          className="w-10 h-10 rounded-md border border-[var(--app-border)] shrink-0"
          style={{ background: /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i.test(branding.color || '') ? branding.color : 'transparent' }}
        />
      </div>
      <Input label="Logo URL" value={branding.logo_url || ''} onChange={(e) => set('logo_url', e.target.value)} />
      <Input label="Launcher icon URL" placeholder="Defaults to the logo" value={branding.launcher_icon_url || ''} onChange={(e) => set('launcher_icon_url', e.target.value)} />
      <Select
        label="Launcher position"
        options={[{ label: 'Bottom right', value: 'bottom-right' }, { label: 'Bottom left', value: 'bottom-left' }]}
        value={branding.position || 'bottom-right'}
        onChange={(value) => set('position', value)}
      />
    </div>
  );
}

function ConversationFields({ branding, set }) {
  return (
    <div className="space-y-3">
      <div className={GRID}>
        <Input label="Greeting" placeholder="Hi — ask me anything about your claims." value={branding.greeting || ''} onChange={(e) => set('greeting', e.target.value)} />
        <Input label="Composer placeholder" placeholder="Ask the Command Center…" value={branding.placeholder || ''} onChange={(e) => set('placeholder', e.target.value)} />
      </div>
      <PromptListEditor prompts={branding.suggested_prompts || []} onChange={(prompts) => set('suggested_prompts', prompts)} />
    </div>
  );
}

function BehaviorFields({ branding, set }) {
  return (
    <div className="flex flex-col gap-3">
      <label className="flex items-center gap-2 text-sm cursor-pointer">
        <Switch checked={!!branding.hide_branding} onChange={(next) => set('hide_branding', !!next)} />
        <span>Hide platform footer</span>
      </label>
      <label className="flex items-center gap-2 text-sm cursor-pointer">
        <Switch checked={branding.allow_expand !== false} onChange={(next) => set('allow_expand', !!next)} />
        <span>Allow visitors to expand the panel</span>
      </label>
      <label className="flex items-center gap-2 text-sm cursor-pointer">
        <Switch checked={branding.feedback !== false} onChange={(next) => set('feedback', !!next)} />
        <span>Show feedback buttons on answers</span>
      </label>
      {/* Names, status and timing only — tool arguments and results never leave the server
          on this surface (routes/embedRouter.js's traces route). */}
      <label className="flex items-center gap-2 text-sm cursor-pointer">
        <Switch checked={branding.show_traces !== false} onChange={(next) => set('show_traces', !!next)} />
        <span>Show which tools ran on each answer</span>
      </label>
      {/* Dictation into the composer, using the visitor's own browser. Only appears where the
          browser supports speech recognition — Firefox has none. */}
      <label className="flex items-center gap-2 text-sm cursor-pointer">
        <Switch checked={branding.voice_input !== false} onChange={(next) => set('voice_input', !!next)} />
        <span>Let visitors dictate messages</span>
      </label>
    </div>
  );
}

/**
 * Per-key Embed SDK v2 config (expandable row): white-label branding (saved onto the
 * key row's `branding` JSON column via generic crud — embed.rotate gates the write)
 * and the end-user identity secret (bespoke generate endpoint, shown once — the host
 * SaaS signs identity JWTs with it, services/embed/embedAuth.js).
 */
function EmbedKeyConfigRow({ row, onSaved, onDeleted }) {
  const keyRow = row;
  const { fetchApi } = useAppApi();
  const initial = (() => {
    try { return JSON.parse(keyRow.branding || '{}') || {}; } catch { return {}; }
  })();
  const [branding, setBranding] = useState(initial);
  const set = (field, value) => setBranding((b) => ({ ...b, [field]: value }));
  const [secret, setSecret] = useState(null);
  const [status, setStatus] = useState('');
  const [err, setErr] = useState('');
  const [deleting, setDeleting] = useState(false);

  const saveBranding = async () => {
    setErr('');
    try {
      const clean = cleanBranding(branding);
      await fetchApi('crud', {
        model: 'AppEmbedKey', action: 'update', where: { id: keyRow.id },
        data: { branding: Object.keys(clean).length ? JSON.stringify(clean) : null },
      });
      setStatus('Saved.');
      onSaved?.();
    } catch (ex) {
      setErr((ex.response && ex.response.data && ex.response.data.error) || ex.message);
    }
  };

  const generateSecret = async () => {
    setErr('');
    try {
      const res = await fetchApi('embedKeyIdentitySecret', keyRow.id);
      setSecret(res.identity_secret);
      onSaved?.();
    } catch (ex) {
      setErr((ex.response && ex.response.data && ex.response.data.error) || ex.message);
    }
  };

  // Deleting a key the widget snippet references breaks that integration immediately
  // (the widget goes "Chat unavailable"), and a key with client providers still bound
  // is refused server-side with a 409 naming them — surfaced below, not swallowed.
  const remove = async () => {
    const label = keyRow.label || keyRow.key;
    if (!window.confirm(`Delete embed key "${label}"? The widget snippet using it stops working immediately. This cannot be undone.`)) return;
    setErr('');
    setDeleting(true);
    try {
      await fetchApi('crud', { model: 'AppEmbedKey', action: 'delete', where: { id: keyRow.id } });
      onDeleted?.();
    } catch (ex) {
      setErr((ex.response && ex.response.data && ex.response.data.error) || ex.message);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-3">
      {err && <Alert variant="error" title="Error">{err}</Alert>}
      {secret && (
        <Alert variant="warning" title="Identity secret — copy it now, it will not be shown again">
          <code className="block break-all text-xs bg-[var(--app-surface)] rounded p-2 my-2">{secret}</code>
          <p className="text-xs">Your backend signs identity JWTs with it: <code>jwt.sign(&#123;sub, email, name&#125;, secret)</code> → pass as <code>data-identity</code> / <code>AgentWidget.identify()</code>.</p>
        </Alert>
      )}
      <FieldGroup title="Appearance"><AppearanceFields branding={branding} set={set} /></FieldGroup>
      <FieldGroup title="Conversation"><ConversationFields branding={branding} set={set} /></FieldGroup>
      <FieldGroup title="Behavior"><BehaviorFields branding={branding} set={set} /></FieldGroup>
      <Can module="embed" action="rotate">
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" onClick={saveBranding}>Save branding</Button>
          <Button size="sm" variant="secondary" onClick={generateSecret}>
            {keyRow.has_identity_secret ? 'Regenerate identity secret' : 'Generate identity secret'}
          </Button>
          <Button size="sm" variant="secondary" onClick={remove} disabled={deleting}>
            <Trash2 size={14} className="mr-1 inline" />{deleting ? 'Deleting…' : 'Delete'}
          </Button>
          {status && <span className="text-xs text-emerald-600">{status}</span>}
          <span className="text-xs text-secondary-500">
            {keyRow.has_identity_secret ? 'Identity handoff enabled for this key.' : 'No identity secret yet — widget users are anonymous.'}
          </span>
        </div>
      </Can>
    </div>
  );
}

function embedKeyColumns() {
  return [
    { key: 'label', label: 'Label', render: (k) => k.label || '—' },
    { key: 'key', label: 'Key', render: (k) => <span className="font-mono text-xs">{k.key}</span> },
    {
      key: 'agent_keys',
      label: 'Agents',
      render: (k) => parseJsonArray(k.agent_keys).join(', ') || '—',
    },
    {
      key: 'allowed_origins',
      label: 'Origins',
      render: (k) => {
        const origins = parseJsonArray(k.allowed_origins);
        return origins.length ? origins.join(', ') : <span className="text-amber-600">any origin</span>;
      },
    },
    {
      key: 'is_active',
      label: 'Status',
      searchable: false,
      render: (k) => (
        <span className={`px-2 py-0.5 rounded text-xs ${k.is_active ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-600'}`}>
          {k.is_active ? 'active' : 'disabled'}
        </span>
      ),
    },
    {
      key: 'demo',
      label: '',
      sortable: false,
      searchable: false,
      render: (k) => {
        const firstAgentKey = parseJsonArray(k.agent_keys)[0];
        if (!firstAgentKey) return null;
        return (
          <a
            href={`/embed-demo?key=${encodeURIComponent(k.key)}&agentKey=${encodeURIComponent(firstAgentKey)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary-600 hover:underline text-xs font-medium"
          >
            Demo
          </a>
        );
      },
    },
  ];
}

export default function EmbedKeysPage() {
  const { fetchApi } = useAppApi();
  const [embedKeys, setEmbedKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [status, setStatus] = useState('');
  const [apps, setApps] = useState([]);
  const [applicationId, setApplicationId] = useState('');
  const [creating, setCreating] = useState(false);

  const reload = useCallback(() => fetchApi('crud', { model: 'AppEmbedKey', action: 'list', limit: 100 })
    .then((res) => setEmbedKeys((Array.isArray(res) ? res : res?.data) || []))
    .catch((ex) => setErr((ex.response && ex.response.data && ex.response.data.error) || ex.message))
    .finally(() => setLoading(false)), []);

  useEffect(() => {
    reload();
    fetchApi('applicationsList')
      .then((res) => setApps((Array.isArray(res) ? res : res?.data) || []))
      .catch(() => setApps([]));
  }, [reload]);

  // Stable identity is load-bearing: an inline arrow would be a NEW component type every
  // render, so <Table> would remount the expanded panel and discard unsaved branding edits.
  const Details = useMemo(
    () => function EmbedKeyDetails({ row }) { return <EmbedKeyConfigRow row={row} onSaved={reload} onDeleted={reload} />; },
    [reload],
  );

  const appOptions = useMemo(() => [
    { label: 'All applications', value: '' },
    ...apps.map((a) => ({ label: a.name, value: String(a.id) })),
  ], [apps]);

  const rows = useMemo(() => {
    let filtered = embedKeys.filter((k) => {
      if (status === 'active') return !!k.is_active;
      if (status === 'disabled') return !k.is_active;
      return true;
    });
    if (applicationId) filtered = filtered.filter((item) => item.application_id == null || item.application_id === Number(applicationId));
    return filtered;
  }, [embedKeys, status, applicationId]);

  return (
    <PageShell className="space-y-4">
      <PageHeader
        title="Embed Keys"
        meta={(
          <p className="text-secondary-500 text-sm">
            Publishable keys that let external websites embed one of your agents as a chat widget (PRD §3.9).
            Empty origins = works on any site (testing only). Expand a row to configure branding and identity handoff, or delete the key.
          </p>
        )}
        actions={(
          <Can module="embed" action={writeActionFor('embed')}>
            <Button size="sm" onClick={() => setCreating((v) => !v)}>New embed key</Button>
          </Can>
        )}
      />

      {creating && (
        <NewEmbedKeyForm onCreated={() => { setCreating(false); reload(); }} onCancel={() => setCreating(false)} />
      )}

      <DataTable
        data={rows}
        columns={embedKeyColumns()}
        loading={loading}
        error={err}
        emptyText="No embed keys yet."
        search={{ placeholder: 'Search keys…', keys: ['label', 'key', 'agent_keys', 'allowed_origins'] }}
        toolbar={(
          <>
            <div className="w-48 sm:w-56"><Select options={appOptions} value={applicationId} onChange={setApplicationId} placeholder="All applications" searchable /></div>
            <div className="w-44"><Select options={STATUS_OPTIONS} value={status} onChange={setStatus} placeholder="All statuses" /></div>
          </>
        )}
        onReload={reload}
        hasDetails
        DetailsComponent={Details}
      />
    </PageShell>
  );
}
